/**
 * Emissor de telemetria do add-in → POST /telemetry/log no backend.
 *
 * Dois canais (ver docs/BACKEND.md):
 *  - standard      → eventos operacionais, SEM conteúdo do documento/prompt.
 *  - llm_interaction → 1 registro por TURNO do usuário (prompt + resposta
 *    final + métricas de custo). Emitido só no desfecho do turno.
 *
 * Regras:
 *  - fire-and-forget: falha de telemetria NUNCA quebra a UI (catch silencioso);
 *  - `session_id` é gerado uma vez por sessão do painel (vida do módulo);
 *  - `turn_id` é gerado a cada envio do usuário (1 turno = N /agent/step).
 *
 * O e-mail NÃO é enviado daqui. Vai o token do Entra no `Authorization`, e o
 * backend preenche o campo com a identidade que ele mesmo validou — um e-mail
 * mandado pelo cliente é rebaixado a `client_email` justamente porque qualquer
 * um poderia escrever log em nome de outra pessoa.
 *
 * E o token usado aqui é SÓ o que já está em cache (`cachedAuthHeader`): emitir
 * log nunca pode disparar uma aquisição de credencial. Quando fazia, um login
 * que falhava por throttling emitia o log da falha, o log pedia token, e esse
 * pedido renovava o throttling — o registro do erro passava a causar o erro.
 * Log sem e-mail é uma perda pequena; log que altera o que observa, não.
 */
import { cachedAuthHeader } from './auth';
import { dlog } from './log';
const SERVER_URL = __SERVER_URL__;
const API_KEY = __API_KEY__;
function generateId(prefix) {
    const c = globalThis.crypto;
    const rnd = (c === null || c === void 0 ? void 0 : c.randomUUID)
        ? c.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    return `${prefix}-${rnd}`;
}
/** ID da sessão do painel (estável enquanto o taskpane estiver aberto). */
export const sessionId = generateId('sess');
/** Novo ID de turno — chame UMA vez por envio do usuário. */
export function newTurnId() {
    return generateId('turn');
}
export function emptyTurnTokens() {
    return {
        simple: { input: 0, output: 0 },
        standard: { input: 0, output: 0 },
        complex: { input: 0, output: 0 },
    };
}
/** Soma o `usage` de um /agent/step no bucket do tier informado pelo backend. */
export function accumulateTierTokens(acc, tier, usage) {
    var _a, _b, _c, _d;
    if (!usage)
        return;
    const bucket = tier === 'simple' ? acc.simple : tier === 'complex' ? acc.complex : acc.standard;
    // input "real" = novo + lido do cache + escrito no cache (tudo é cobrado)
    bucket.input +=
        ((_a = usage.input_tokens) !== null && _a !== void 0 ? _a : 0) +
            ((_b = usage.cache_read_input_tokens) !== null && _b !== void 0 ? _b : 0) +
            ((_c = usage.cache_creation_input_tokens) !== null && _c !== void 0 ? _c : 0);
    bucket.output += (_d = usage.output_tokens) !== null && _d !== void 0 ? _d : 0;
}
/**
 * Teto do `keepalive`: o navegador RECUSA (TypeError) uma requisição keepalive
 * com corpo acima de 64 KiB — e o `catch` abaixo engolia isso, então o registro
 * de auditoria de um turno longo (prompt + resposta) se perdia calado. Acima do
 * teto vai sem keepalive: chega sempre, só não sobrevive ao painel fechando.
 */
const KEEPALIVE_MAX_BYTES = 60000;
async function post(payload) {
    try {
        const body = JSON.stringify(payload);
        const resp = await fetch(`${SERVER_URL}/telemetry/log`, {
            method: 'POST',
            headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (API_KEY ? { 'X-API-Key': API_KEY } : {})), cachedAuthHeader()),
            body,
            // keepalive: tenta entregar mesmo se o painel estiver fechando
            keepalive: new Blob([body]).size <= KEEPALIVE_MAX_BYTES,
        });
        // Um 422 (payload fora do contrato) não lança e antes sumia sem deixar
        // rastro NENHUM no cliente — foi assim que `validation.run` ficou sendo
        // descartado sem ninguém perceber. Em produção `dlog` é no-op, então isto
        // não vaza nada; em dev, aparece no console.
        if (!resp.ok) {
            dlog('[telemetry] recusado pelo backend:', resp.status, await resp.text().catch(() => ''));
        }
    }
    catch (e) {
        dlog('[telemetry] envio falhou (ignorado):', e);
    }
}
/** Evento operacional (canal standard). Fire-and-forget. */
export function logStandard(ev) {
    var _a;
    void post({
        channel: 'standard',
        entry: Object.assign({ session_id: sessionId, component: (_a = ev.component) !== null && _a !== void 0 ? _a : 'frontend' }, ev),
    });
}
/** Registro de turno (canal llm_interaction). Chame UMA vez por turno. */
export function logLlmInteraction(ev) {
    void post({
        channel: 'llm_interaction',
        entry: Object.assign({ session_id: sessionId }, ev),
    });
}
